/**
 * Annotation Properties Panel
 * 
 * Right sidebar in the Annotation Editor for editing selected annotation properties.
 * Title Box includes all the existing TitleBoxDialog fields for full compatibility.
 */

import React, { useState, useCallback } from 'react';
import {
  Box,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Slider,
  Button,
  Divider,
  Switch,
  FormControlLabel,
  IconButton,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Paper,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import UploadIcon from '@mui/icons-material/Upload';
import type {
  PageAnnotation,
  TitleBoxAnnotation,
  NorthArrowAnnotation,
  LegendAnnotation,
  ManifestAnnotation,
  NotesAnnotation,
  ScaleBarAnnotation,
  NorthArrowStyle,
} from '../../types/annotations';
import { SPACING_REQUIREMENTS } from '../../types/annotations';

interface AnnotationPropertiesPanelProps {
  annotation: PageAnnotation | null;
  onUpdate: (id: string, updates: Partial<PageAnnotation>) => void;
  onDelete: (id: string) => void;
}

// Title Box Properties - Full form matching TitleBoxDialog
const TitleBoxProperties: React.FC<{
  data: TitleBoxAnnotation;
  onUpdate: (updates: Partial<TitleBoxAnnotation>) => void;
  onDelete: () => void;
}> = ({ data, onUpdate, onDelete }) => {
  const handleLogoUpload = (position: 'left' | 'center' | 'right', file: File) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      onUpdate({ logos: { ...data.logos, [position]: reader.result as string } });
    };
    reader.readAsDataURL(file);
  };

  const handleLogoRemove = (position: 'left' | 'center' | 'right') => {
    onUpdate({ logos: { ...data.logos, [position]: null } });
  };

  const LogoUploader: React.FC<{ position: 'left' | 'center' | 'right'; label: string; show: boolean; onToggle: (v: boolean) => void }> = ({ position, label, show, onToggle }) => (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
        <Typography variant="caption" sx={{ color: '#000000' }}>{label}</Typography>
        <Switch size="small" checked={show} onChange={(e) => onToggle(e.target.checked)} />
      </Box>
      {show && (
        <Paper
          variant="outlined"
          sx={{
            p: 1,
            textAlign: 'center',
            backgroundColor: '#FFFFFF',
            borderColor: '#D0D0D0',
          }}
        >
          {data.logos[position] ? (
            <Box>
              <Box component="img" src={data.logos[position]!} sx={{ maxWidth: '100%', maxHeight: 40, mb: 0.5 }} />
              <IconButton size="small" onClick={() => handleLogoRemove(position)} sx={{ color: '#DC2626' }}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          ) : (
            <>
              <input
                accept="image/*"
                style={{ display: 'none' }}
                id={`logo-${position}`}
                type="file"
                onChange={(e) => e.target.files?.[0] && handleLogoUpload(position, e.target.files[0])}
              />
              <label htmlFor={`logo-${position}`}>
                <Button component="span" size="small" startIcon={<UploadIcon />} sx={{ textTransform: 'none', color: '#000000' }}>
                  Upload
                </Button>
              </label>
            </>
          )}
        </Paper>
      )}
    </Box>
  );

  return (
    <Box sx={{ p: 2, height: '100%', overflowY: 'auto' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="subtitle1" sx={{ color: '#000000', fontWeight: 600 }}>Title Box</Typography>
        <Tooltip title="Delete">
          <IconButton size="small" onClick={onDelete} sx={{ color: '#DC2626' }}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Enable/Disable */}
      <FormControlLabel
        control={<Switch checked={data.useTitleBox} onChange={(e) => onUpdate({ useTitleBox: e.target.checked })} />}
        label={<Typography sx={{ fontSize: '0.875rem', color: '#000000' }}>Include Title Box</Typography>}
        sx={{ mb: 2 }}
      />

      {data.useTitleBox && (
        <>
          {/* Logos */}
          <Accordion defaultExpanded={false} sx={{ backgroundColor: 'transparent', boxShadow: 'none', '&:before': { display: 'none' } }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: '#000000' }} />}>
              <Typography sx={{ color: '#000000', fontSize: '0.8rem', fontWeight: 600 }}>LOGOS</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <LogoUploader position="left" label="Left Logo" show={data.showLogoLeft} onToggle={(v) => onUpdate({ showLogoLeft: v })} />
              <LogoUploader position="center" label="Center Logo" show={data.showLogoCenter} onToggle={(v) => onUpdate({ showLogoCenter: v })} />
              <LogoUploader position="right" label="Right Logo" show={data.showLogoRight} onToggle={(v) => onUpdate({ showLogoRight: v })} />
            </AccordionDetails>
          </Accordion>

          {/* Road Type - Controls spacing table header */}
          <TextField
            size="small"
            fullWidth
            label="Road Type (Spacing Table Header)"
            value={data.roadType}
            onChange={(e) => onUpdate({ roadType: e.target.value })}
            placeholder="e.g., TWO WAY - TWO LANE ROADS"
            sx={{ mb: 2 }}
          />

          {/* Project Information */}
          <Accordion defaultExpanded sx={{ backgroundColor: 'transparent', boxShadow: 'none', '&:before': { display: 'none' } }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: '#000000' }} />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                <Typography sx={{ color: '#000000', fontSize: '0.8rem', fontWeight: 600, flex: 1 }}>PROJECT INFO</Typography>
                <Switch size="small" checked={data.showMetadataSection} onChange={(e) => { e.stopPropagation(); onUpdate({ showMetadataSection: e.target.checked }); }} onClick={(e) => e.stopPropagation()} />
              </Box>
            </AccordionSummary>
            {data.showMetadataSection && (
              <AccordionDetails sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <TextField size="small" label="TCP #" value={data.tcpNumber} onChange={(e) => onUpdate({ tcpNumber: e.target.value })} sx={{ flex: 1 }} />
                  <TextField size="small" label="Client" value={data.client} onChange={(e) => onUpdate({ client: e.target.value })} sx={{ flex: 1 }} />
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <TextField size="small" label="Contact" value={data.contact} onChange={(e) => onUpdate({ contact: e.target.value })} sx={{ flex: 1 }} />
                  <TextField size="small" label="Phone" value={data.phone} onChange={(e) => onUpdate({ phone: e.target.value })} sx={{ flex: 1 }} />
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <TextField size="small" label="Location" value={data.location} onChange={(e) => onUpdate({ location: e.target.value })} sx={{ flex: 1 }} />
                  <TextField size="small" label="Council" value={data.council} onChange={(e) => onUpdate({ council: e.target.value })} sx={{ flex: 1 }} />
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <TextField size="small" label="Start Date" type="date" value={data.startDate} onChange={(e) => onUpdate({ startDate: e.target.value })} InputLabelProps={{ shrink: true }} sx={{ flex: 1 }} />
                  <TextField size="small" label="Finish Date" type="date" value={data.finishDate} onChange={(e) => onUpdate({ finishDate: e.target.value })} InputLabelProps={{ shrink: true }} sx={{ flex: 1 }} />
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <TextField size="small" label="Start Time" type="time" value={data.startTime} onChange={(e) => onUpdate({ startTime: e.target.value })} InputLabelProps={{ shrink: true }} sx={{ flex: 1 }} />
                  <TextField size="small" label="Finish Time" type="time" value={data.finishTime} onChange={(e) => onUpdate({ finishTime: e.target.value })} InputLabelProps={{ shrink: true }} sx={{ flex: 1 }} />
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <TextField size="small" label="Author" value={data.author} onChange={(e) => onUpdate({ author: e.target.value })} sx={{ flex: 1 }} />
                  <TextField size="small" label="Modified By" value={data.modifiedBy} onChange={(e) => onUpdate({ modifiedBy: e.target.value })} sx={{ flex: 1 }} />
                </Box>
              </AccordionDetails>
            )}
          </Accordion>

          {/* TGS Requirements */}
          <Accordion defaultExpanded sx={{ backgroundColor: 'transparent', boxShadow: 'none', '&:before': { display: 'none' } }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: '#000000' }} />}>
              <Typography sx={{ color: '#000000', fontSize: '0.8rem', fontWeight: 600 }}>TGS REQUIREMENTS</Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <FormControl size="small" fullWidth>
                <InputLabel>State</InputLabel>
                <Select value={data.state} label="State" onChange={(e) => onUpdate({ state: e.target.value })}>
                  <MenuItem value="SA">South Australia (SA)</MenuItem>
                  <MenuItem value="NSW">New South Wales (NSW)</MenuItem>
                  <MenuItem value="VIC">Victoria (VIC)</MenuItem>
                  <MenuItem value="QLD">Queensland (QLD)</MenuItem>
                  <MenuItem value="WA">Western Australia (WA)</MenuItem>
                  <MenuItem value="TAS">Tasmania (TAS)</MenuItem>
                  <MenuItem value="NT">Northern Territory (NT)</MenuItem>
                  <MenuItem value="ACT">Australian Capital Territory (ACT)</MenuItem>
                </Select>
              </FormControl>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField size="small" label="Zone Length (m)" value={data.lengthOfZone} onChange={(e) => onUpdate({ lengthOfZone: e.target.value })} sx={{ flex: 1 }} />
                <FormControl size="small" sx={{ flex: 1 }}>
                  <InputLabel>Speed (km/h)</InputLabel>
                  <Select value={data.roadSpeed} label="Speed (km/h)" onChange={(e) => onUpdate({ roadSpeed: e.target.value })}>
                    <MenuItem value="40">40</MenuItem>
                    <MenuItem value="50">50</MenuItem>
                    <MenuItem value="60">60</MenuItem>
                    <MenuItem value="70">70</MenuItem>
                    <MenuItem value="80-110">80-110</MenuItem>
                  </Select>
                </FormControl>
              </Box>
              <FormControlLabel
                control={<Switch size="small" checked={data.showSpacingTable} onChange={(e) => onUpdate({ showSpacingTable: e.target.checked })} />}
                label={<Typography sx={{ fontSize: '0.8rem', color: '#000000' }}>Show Spacing Table</Typography>}
              />
            </AccordionDetails>
          </Accordion>

          {/* Spacing Table Values Editor */}
          {data.showSpacingTable && (
            <Accordion defaultExpanded={false} sx={{ backgroundColor: 'transparent', boxShadow: 'none', '&:before': { display: 'none' } }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: '#000000' }} />}>
                <Typography sx={{ color: '#000000', fontSize: '0.8rem', fontWeight: 600 }}>EDIT SPACING VALUES</Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography variant="caption" sx={{ color: '#666', mb: 1 }}>
                  Override default spacing values. Leave blank to use state defaults.
                </Typography>
                {(() => {
                  const stateData = SPACING_REQUIREMENTS[data.state as keyof typeof SPACING_REQUIREMENTS] || SPACING_REQUIREMENTS.SA;
                  const speeds = stateData.speeds.map(String);
                  const rowConfigs = [
                    { key: 'lengthOfZone', label: 'Length of Zone (m)', defaults: Array(5).fill(data.lengthOfZone || '—') },
                    { key: 'bufferZone', label: 'Buffer Zone', defaults: stateData.bufferZone },
                    { key: 'advanceWarning', label: 'Advance Warning', defaults: stateData.advanceWarning },
                    { key: 'taperArea', label: 'Taper Area', defaults: stateData.taperArea },
                    { key: 'safetyBuffer', label: 'Safety Buffer', defaults: stateData.safetyBuffer },
                    { key: 'terminationArea', label: 'Termination Area', defaults: stateData.terminationArea },
                  ];
                  
                  return rowConfigs.map((row) => (
                    <Box key={row.key} sx={{ mb: 1.5 }}>
                      <Typography variant="caption" sx={{ color: '#000', fontWeight: 500, display: 'block', mb: 0.5 }}>
                        {row.label}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {speeds.map((speed, i) => {
                          const customKey = `${data.state}_${row.key}_${i}`;
                          const customValue = data.customSpacingRequirements?.[customKey] || '';
                          const defaultValue = row.defaults[i] || '—';
                          return (
                            <Box key={i} sx={{ flex: '1 1 45%', minWidth: 80 }}>
                              <TextField
                                size="small"
                                placeholder={defaultValue}
                                value={customValue}
                                onChange={(e) => {
                                  const newCustom = { ...data.customSpacingRequirements, [customKey]: e.target.value };
                                  // Remove empty values to fall back to defaults
                                  if (!e.target.value) delete newCustom[customKey];
                                  onUpdate({ customSpacingRequirements: newCustom });
                                }}
                                InputProps={{
                                  sx: { fontSize: '0.75rem' }
                                }}
                                sx={{
                                  '& .MuiInputBase-input::placeholder': { fontSize: '0.7rem', opacity: 0.7 },
                                }}
                                fullWidth
                                label={`${speed} km/h`}
                              />
                            </Box>
                          );
                        })}
                      </Box>
                    </Box>
                  ));
                })()}
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => onUpdate({ customSpacingRequirements: {} })}
                  sx={{ mt: 1, textTransform: 'none', color: '#666', borderColor: '#ccc' }}
                >
                  Reset to Defaults
                </Button>
              </AccordionDetails>
            </Accordion>
          )}
        </>
      )}
    </Box>
  );
};

// North Arrow Properties
const NorthArrowProperties: React.FC<{
  data: NorthArrowAnnotation;
  onUpdate: (updates: Partial<NorthArrowAnnotation>) => void;
  onDelete: () => void;
}> = ({ data, onUpdate, onDelete }) => (
  <Box sx={{ p: 2 }}>
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
      <Typography variant="subtitle1" sx={{ color: '#000000', fontWeight: 600 }}>North Arrow</Typography>
      <IconButton size="small" onClick={onDelete} sx={{ color: '#DC2626' }}><DeleteIcon fontSize="small" /></IconButton>
    </Box>
    <FormControl size="small" fullWidth sx={{ mb: 2 }}>
      <InputLabel>Style</InputLabel>
      <Select value={data.style} label="Style" onChange={(e) => onUpdate({ style: e.target.value as NorthArrowStyle })}>
        <MenuItem value="arrow">Arrow</MenuItem>
        <MenuItem value="star">Star</MenuItem>
        <MenuItem value="compass">Compass</MenuItem>
        <MenuItem value="simple">Simple</MenuItem>
      </Select>
    </FormControl>
    <Box sx={{ mb: 2 }}>
      <Typography variant="caption" sx={{ color: '#000000', display: 'block', mb: 1 }}>Rotation: {data.rotation}°</Typography>
      <Slider value={data.rotation} onChange={(_, v) => onUpdate({ rotation: v as number })} min={0} max={360} sx={{ color: '#DC2626' }} />
    </Box>
  </Box>
);

// Legend Properties
const LegendProperties: React.FC<{
  data: LegendAnnotation;
  onUpdate: (updates: Partial<LegendAnnotation>) => void;
  onDelete: () => void;
}> = ({ data, onUpdate, onDelete }) => {
  const [newLabel, setNewLabel] = React.useState('');
  const [newCount, setNewCount] = React.useState(1);

  const handleAddEntry = () => {
    if (!newLabel.trim()) return;
    const newEntry = { icon: '', label: newLabel.trim(), count: newCount };
    onUpdate({ entries: [...data.entries, newEntry] });
    setNewLabel('');
    setNewCount(1);
  };

  const handleDeleteEntry = (index: number) => {
    const newEntries = data.entries.filter((_, i) => i !== index);
    onUpdate({ entries: newEntries });
  };

  const handleUpdateEntry = (index: number, updates: Partial<{ label: string; count: number }>) => {
    const newEntries = data.entries.map((entry, i) => 
      i === index ? { ...entry, ...updates } : entry
    );
    onUpdate({ entries: newEntries });
  };

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="subtitle1" sx={{ color: '#000000', fontWeight: 600 }}>Legend</Typography>
        <IconButton size="small" onClick={onDelete} sx={{ color: '#DC2626' }}><DeleteIcon fontSize="small" /></IconButton>
      </Box>
      <TextField size="small" fullWidth label="Title" value={data.title} onChange={(e) => onUpdate({ title: e.target.value })} sx={{ mb: 2 }} />
      <FormControlLabel
        control={<Switch size="small" checked={data.showCounts} onChange={(e) => onUpdate({ showCounts: e.target.checked })} />}
        label={<Typography sx={{ fontSize: '0.875rem', color: '#000000' }}>Show counts</Typography>}
        sx={{ mb: 2 }}
      />
      
      {/* Entries List - Directly Editable */}
      <Typography variant="caption" sx={{ color: '#666', fontWeight: 600, display: 'block', mb: 1 }}>
        ITEMS ({data.entries.length})
      </Typography>
      <Box sx={{ maxHeight: 250, overflowY: 'auto', mb: 2 }}>
        {data.entries.map((entry, idx) => (
          <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
            <TextField
              size="small"
              value={entry.label}
              onChange={(e) => handleUpdateEntry(idx, { label: e.target.value })}
              placeholder="Item label"
              sx={{ flex: 1, '& input': { fontSize: 12, py: 0.75 } }}
            />
            <TextField
              size="small"
              type="number"
              value={entry.count}
              onChange={(e) => handleUpdateEntry(idx, { count: parseInt(e.target.value) || 1 })}
              sx={{ width: 55, '& input': { fontSize: 12, py: 0.75, textAlign: 'center' } }}
            />
            <IconButton size="small" onClick={() => handleDeleteEntry(idx)} sx={{ p: 0.5, color: '#DC2626' }}>
              <DeleteIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Box>
        ))}
        
        {/* Add New Entry Row */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1, pt: 1, borderTop: data.entries.length > 0 ? '1px dashed #e0e0e0' : 'none' }}>
          <TextField
            size="small"
            value={newLabel}
            onChange={(e) => setNewLabel(e.target.value)}
            placeholder="Add new item..."
            sx={{ flex: 1, '& input': { fontSize: 12, py: 0.75 } }}
            onKeyPress={(e) => e.key === 'Enter' && handleAddEntry()}
          />
          <TextField
            size="small"
            type="number"
            value={newCount}
            onChange={(e) => setNewCount(parseInt(e.target.value) || 1)}
            sx={{ width: 55, '& input': { fontSize: 12, py: 0.75, textAlign: 'center' } }}
          />
          <IconButton 
            size="small" 
            onClick={handleAddEntry}
            disabled={!newLabel.trim()}
            sx={{ p: 0.5, color: newLabel.trim() ? '#000' : '#ccc' }}
          >
            <Typography sx={{ fontSize: 16, fontWeight: 600 }}>+</Typography>
          </IconButton>
        </Box>
      </Box>
    </Box>
  );
};

// Manifest Properties
const ManifestProperties: React.FC<{
  data: ManifestAnnotation;
  onUpdate: (updates: Partial<ManifestAnnotation>) => void;
  onDelete: () => void;
}> = ({ data, onUpdate, onDelete }) => {
  const [newItem, setNewItem] = React.useState('');
  const [newQty, setNewQty] = React.useState(1);
  const [newUnit, setNewUnit] = React.useState('ea');

  const handleAddEntry = () => {
    if (!newItem.trim()) return;
    const newEntry = { item: newItem.trim(), quantity: newQty, unit: newUnit };
    onUpdate({ entries: [...data.entries, newEntry] });
    setNewItem('');
    setNewQty(1);
  };

  const handleDeleteEntry = (index: number) => {
    const newEntries = data.entries.filter((_, i) => i !== index);
    onUpdate({ entries: newEntries });
  };

  const handleUpdateEntry = (index: number, updates: Partial<{ item: string; quantity: number; unit: string }>) => {
    const newEntries = data.entries.map((entry, i) => 
      i === index ? { ...entry, ...updates } : entry
    );
    onUpdate({ entries: newEntries });
  };

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="subtitle1" sx={{ color: '#000000', fontWeight: 600 }}>Equipment</Typography>
        <IconButton size="small" onClick={onDelete} sx={{ color: '#DC2626' }}><DeleteIcon fontSize="small" /></IconButton>
      </Box>
      <TextField size="small" fullWidth label="Title" value={data.title} onChange={(e) => onUpdate({ title: e.target.value })} sx={{ mb: 2 }} />
      <FormControlLabel
        control={<Switch size="small" checked={data.groupByCategory} onChange={(e) => onUpdate({ groupByCategory: e.target.checked })} />}
        label={<Typography sx={{ fontSize: '0.875rem', color: '#000000' }}>Group by category</Typography>}
        sx={{ mb: 1 }}
      />
      <FormControlLabel
        control={<Switch size="small" checked={data.showTotal} onChange={(e) => onUpdate({ showTotal: e.target.checked })} />}
        label={<Typography sx={{ fontSize: '0.875rem', color: '#000000' }}>Show total</Typography>}
        sx={{ mb: 2 }}
      />

      {/* Entries List - Directly Editable */}
      <Typography variant="caption" sx={{ color: '#666', fontWeight: 600, display: 'block', mb: 1 }}>
        ITEMS ({data.entries.length})
      </Typography>
      <Box sx={{ maxHeight: 250, overflowY: 'auto', mb: 2 }}>
        {data.entries.map((entry, idx) => (
          <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
            <TextField
              size="small"
              value={entry.item}
              onChange={(e) => handleUpdateEntry(idx, { item: e.target.value })}
              placeholder="Item name"
              sx={{ flex: 1, '& input': { fontSize: 12, py: 0.75 } }}
            />
            <TextField
              size="small"
              type="number"
              value={entry.quantity}
              onChange={(e) => handleUpdateEntry(idx, { quantity: parseInt(e.target.value) || 1 })}
              sx={{ width: 50, '& input': { fontSize: 12, py: 0.75, textAlign: 'center' } }}
            />
            <TextField
              size="small"
              value={entry.unit}
              onChange={(e) => handleUpdateEntry(idx, { unit: e.target.value })}
              sx={{ width: 45, '& input': { fontSize: 12, py: 0.75, textAlign: 'center' } }}
            />
            <IconButton size="small" onClick={() => handleDeleteEntry(idx)} sx={{ p: 0.5, color: '#DC2626' }}>
              <DeleteIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Box>
        ))}
        
        {/* Add New Entry Row */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1, pt: 1, borderTop: data.entries.length > 0 ? '1px dashed #e0e0e0' : 'none' }}>
          <TextField
            size="small"
            value={newItem}
            onChange={(e) => setNewItem(e.target.value)}
            placeholder="Add new item..."
            sx={{ flex: 1, '& input': { fontSize: 12, py: 0.75 } }}
            onKeyPress={(e) => e.key === 'Enter' && handleAddEntry()}
          />
          <TextField
            size="small"
            type="number"
            value={newQty}
            onChange={(e) => setNewQty(parseInt(e.target.value) || 1)}
            sx={{ width: 50, '& input': { fontSize: 12, py: 0.75, textAlign: 'center' } }}
          />
          <TextField
            size="small"
            value={newUnit}
            onChange={(e) => setNewUnit(e.target.value)}
            sx={{ width: 45, '& input': { fontSize: 12, py: 0.75, textAlign: 'center' } }}
          />
          <IconButton 
            size="small" 
            onClick={handleAddEntry}
            disabled={!newItem.trim()}
            sx={{ p: 0.5, color: newItem.trim() ? '#000' : '#ccc' }}
          >
            <Typography sx={{ fontSize: 16, fontWeight: 600 }}>+</Typography>
          </IconButton>
        </Box>
      </Box>
    </Box>
  );
};

// Notes Properties
const NotesProperties: React.FC<{
  data: NotesAnnotation;
  onUpdate: (updates: Partial<NotesAnnotation>) => void;
  onDelete: () => void;
}> = ({ data, onUpdate, onDelete }) => (
  <Box sx={{ p: 2 }}>
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
      <Typography variant="subtitle1" sx={{ color: '#000000', fontWeight: 600 }}>Notes</Typography>
      <IconButton size="small" onClick={onDelete} sx={{ color: '#DC2626' }}><DeleteIcon fontSize="small" /></IconButton>
    </Box>
    <TextField size="small" fullWidth label="Title" value={data.title} onChange={(e) => onUpdate({ title: e.target.value })} sx={{ mb: 2 }} />
    <TextField size="small" fullWidth label="Content" value={data.content} onChange={(e) => onUpdate({ content: e.target.value })} multiline rows={4} sx={{ mb: 2 }} />
    <Box sx={{ mb: 2 }}>
      <Typography variant="caption" sx={{ color: '#000000', display: 'block', mb: 1 }}>Font Size: {data.fontSize}px</Typography>
      <Slider value={data.fontSize} onChange={(_, v) => onUpdate({ fontSize: v as number })} min={8} max={24} sx={{ color: '#DC2626' }} />
    </Box>
    <FormControlLabel
      control={<Switch size="small" checked={data.showBorder} onChange={(e) => onUpdate({ showBorder: e.target.checked })} />}
      label={<Typography sx={{ fontSize: '0.875rem', color: '#000000' }}>Show border</Typography>}
    />
  </Box>
);

// Scale Bar Properties
const ScaleBarProperties: React.FC<{
  data: ScaleBarAnnotation;
  onUpdate: (updates: Partial<ScaleBarAnnotation>) => void;
  onDelete: () => void;
}> = ({ data, onUpdate, onDelete }) => (
  <Box sx={{ p: 2 }}>
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
      <Typography variant="subtitle1" sx={{ color: '#000000', fontWeight: 600 }}>Scale Bar</Typography>
      <IconButton size="small" onClick={onDelete} sx={{ color: '#DC2626' }}><DeleteIcon fontSize="small" /></IconButton>
    </Box>
    <TextField size="small" fullWidth label="Scale" value={data.scale} onChange={(e) => onUpdate({ scale: e.target.value })} placeholder="1:500" sx={{ mb: 2 }} />
    <FormControl size="small" fullWidth sx={{ mb: 2 }}>
      <InputLabel>Units</InputLabel>
      <Select value={data.units} label="Units" onChange={(e) => onUpdate({ units: e.target.value as 'metric' | 'imperial' })}>
        <MenuItem value="metric">Metric (m)</MenuItem>
        <MenuItem value="imperial">Imperial (ft)</MenuItem>
      </Select>
    </FormControl>
    <FormControl size="small" fullWidth sx={{ mb: 2 }}>
      <InputLabel>Style</InputLabel>
      <Select value={data.style} label="Style" onChange={(e) => onUpdate({ style: e.target.value as 'bar' | 'line' | 'alternating' })}>
        <MenuItem value="alternating">Alternating</MenuItem>
        <MenuItem value="bar">Solid Bar</MenuItem>
        <MenuItem value="line">Line</MenuItem>
      </Select>
    </FormControl>
  </Box>
);

export const AnnotationPropertiesPanel: React.FC<AnnotationPropertiesPanelProps> = ({
  annotation,
  onUpdate,
  onDelete,
}) => {
  const handleUpdate = useCallback((updates: Partial<PageAnnotation>) => {
    if (annotation) {
      onUpdate(annotation.id, updates);
    }
  }, [annotation, onUpdate]);

  const handleDelete = useCallback(() => {
    if (annotation) {
      onDelete(annotation.id);
    }
  }, [annotation, onDelete]);

  return (
    <Box
      sx={{
        width: 320,
        backgroundColor: '#F5F5F5',
        borderLeft: '1px solid #E0E0E0',
        boxShadow: '-2px 0 8px rgba(0,0,0,0.1)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      {annotation ? (
        <Box sx={{ flex: 1, overflowY: 'auto' }}>
          {/* Size controls - common for all */}
          <Box sx={{ p: 2, borderBottom: '1px solid #E0E0E0', backgroundColor: '#FAFAFA' }}>
            <Typography variant="overline" sx={{ color: '#000000', fontSize: '0.65rem', fontWeight: 600 }}>SIZE & POSITION</Typography>
            <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
              <TextField
                size="small"
                label="X"
                type="number"
                value={Math.round(annotation.position.x)}
                onChange={(e) => handleUpdate({ position: { ...annotation.position, x: parseInt(e.target.value) || 0 } })}
                sx={{ flex: 1 }}
              />
              <TextField
                size="small"
                label="Y"
                type="number"
                value={Math.round(annotation.position.y)}
                onChange={(e) => handleUpdate({ position: { ...annotation.position, y: parseInt(e.target.value) || 0 } })}
                sx={{ flex: 1 }}
              />
            </Box>
            <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
              <TextField
                size="small"
                label="Width"
                type="number"
                value={Math.round(annotation.width)}
                onChange={(e) => handleUpdate({ width: parseInt(e.target.value) || 100 })}
                sx={{ flex: 1 }}
              />
              <TextField
                size="small"
                label="Height"
                type="number"
                value={Math.round(annotation.height)}
                onChange={(e) => handleUpdate({ height: parseInt(e.target.value) || 100 })}
                sx={{ flex: 1 }}
              />
            </Box>
          </Box>

          {/* Type-specific properties */}
          {annotation.type === 'title-box' && (
            <TitleBoxProperties data={annotation as TitleBoxAnnotation} onUpdate={handleUpdate} onDelete={handleDelete} />
          )}
          {annotation.type === 'north-arrow' && (
            <NorthArrowProperties data={annotation as NorthArrowAnnotation} onUpdate={handleUpdate} onDelete={handleDelete} />
          )}
          {annotation.type === 'legend' && (
            <LegendProperties data={annotation as LegendAnnotation} onUpdate={handleUpdate} onDelete={handleDelete} />
          )}
          {annotation.type === 'manifest' && (
            <ManifestProperties data={annotation as ManifestAnnotation} onUpdate={handleUpdate} onDelete={handleDelete} />
          )}
          {annotation.type === 'notes' && (
            <NotesProperties data={annotation as NotesAnnotation} onUpdate={handleUpdate} onDelete={handleDelete} />
          )}
          {annotation.type === 'scale-bar' && (
            <ScaleBarProperties data={annotation as ScaleBarAnnotation} onUpdate={handleUpdate} onDelete={handleDelete} />
          )}
        </Box>
      ) : (
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body2" sx={{ color: '#000000' }}>
            Select an annotation to edit its properties
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default AnnotationPropertiesPanel;
